import type { Ref, ComputedRef } from 'vue'
import type { V1alpha1Workspace, V1ObjectMeta } from '@streamnative/cloud-api-client-typescript'
import { listWorkspaces, deleteWorkspace } from '@/api/workspace'
import {
  listAgentsForWorkspace,
  listFunctionsForWorkspace,
  listSinksForWorkspace,
  listSourcesForWorkspace
} from '@/api/registry-admin'
import type { PulsarState } from './usePulsarState'

export interface WorkspaceConditions {
  Ready: boolean
  Available: boolean
}

export interface Workspace extends V1alpha1Workspace {
  metadata: V1ObjectMeta & {
    name: string
    namespace: string
  }
  generated: {
    conditions: WorkspaceConditions
    deleted: boolean
  }
}

export interface WorkspaceResourceCounts {
  agents?: number
  functions?: number
  connectors?: number
}

export const formatWorkspaceModel = (ws: V1alpha1Workspace): Workspace => {
  const conditions: WorkspaceConditions = {
    Ready: false,
    Available: false
  }

  if (ws.status?.conditions) {
    for (const c of ws.status.conditions) {
      if (c.type === 'Ready') {
        conditions.Ready = c.status === 'True'
        conditions.Available = c.status === 'True'
      }
    }
  }

  return {
    ...ws,
    metadata: ws.metadata as Workspace['metadata'],
    generated: {
      conditions,
      deleted: !!ws.metadata?.deletionTimestamp
    }
  }
}

let lastOrg: string | undefined = undefined

export const workspaceMap: Ref<Record<string, Workspace[]>> = ref({})
const resourceCounts = ref<Record<string, WorkspaceResourceCounts>>({})

const getWorkspaceMap = async (organization: string) => {
  try {
    const items = await listWorkspaces(organization)
    const map: Record<string, Workspace[]> = {}
    for (const ws of items) {
      const instanceName = ws.spec?.instanceName ?? ''
      if (!map[instanceName]) {
        map[instanceName] = []
      }
      map[instanceName].push(formatWorkspaceModel(ws))
    }
    workspaceMap.value = map
  } catch {
    workspaceMap.value = {}
  }
}

const instanceWorkspaces = computed(() => {
  const { instance } = usePulsarState()
  if (!instance.value || !workspaceMap.value[instance.value]) {
    return []
  }
  return workspaceMap.value[instance.value].sort((a, b) => {
    return (a.metadata?.name ?? '').localeCompare(b.metadata?.name ?? '')
  })
})

const activeWorkspace: ComputedRef<Workspace | undefined> = computed(() => {
  const { instance, workspaceUid } = usePulsarState()
  if (!instance.value) {
    return undefined
  }

  if (!workspaceMap.value[instance.value]?.length) {
    return undefined
  }

  return workspaceMap.value[instance.value]?.find(ws => {
    return ws.metadata?.uid === workspaceUid.value
  })
})

const activeWorkspaceName = computed(() => activeWorkspace.value?.metadata?.name)

export const useWorkspace = () => {
  const { mustOrganization } = usePulsarState()

  const getWorkspaces = async () => {
    await getWorkspaceMap(mustOrganization())
  }

  const getResourceCounts = async (ws: Workspace) => {
    const uid = ws.metadata?.uid
    if (!uid) return

    try {
      const org = mustOrganization()
      const [agents, functions, sinks, sources] = await Promise.all([
        listAgentsForWorkspace(org, uid)
          .then(r => r.data?.length ?? 0)
          .catch(() => 0),
        listFunctionsForWorkspace(org, uid)
          .then(r => r.data?.length ?? 0)
          .catch(() => 0),
        listSinksForWorkspace(org, uid)
          .then(r => r.data?.length ?? 0)
          .catch(() => 0),
        listSourcesForWorkspace(org, uid)
          .then(r => r.data?.length ?? 0)
          .catch(() => 0)
      ])
      resourceCounts.value[uid] = {
        agents,
        functions,
        connectors: sinks + sources
      }
    } catch {
      resourceCounts.value[uid] = { agents: 0, functions: 0, connectors: 0 }
    }
  }

  const getWorkspaceResourceCounts = (ws: Workspace): WorkspaceResourceCounts => {
    return resourceCounts.value[ws.metadata?.uid ?? ''] ?? {}
  }

  const isWorkspaceReady = (ws: Workspace): boolean => {
    return ws.generated.conditions.Ready
  }

  const getWorkspaceStatus = (ws: Workspace): string => {
    return isWorkspaceReady(ws) ? 'Active' : 'Provisioning'
  }

  const getWorkspaceStatusColor = (ws: Workspace): 'positive' | 'informational' => {
    return isWorkspaceReady(ws) ? 'positive' : 'informational'
  }

  const setWorkspaceDeleted = (ws: Workspace) => {
    ws.generated.deleted = true
  }

  const deleteWorkspaceByName = async (name: string) => {
    await deleteWorkspace(name, mustOrganization())
  }

  return {
    workspaceMap,
    resourceCounts,
    instanceWorkspaces,
    activeWorkspace,
    activeWorkspaceName,
    getWorkspaces,
    getResourceCounts,
    getWorkspaceResourceCounts,
    isWorkspaceReady,
    getWorkspaceStatus,
    getWorkspaceStatusColor,
    setWorkspaceDeleted,
    deleteWorkspaceByName,
    init
  }
}

const init = (initialState: PulsarState) => {
  const { organization } = usePulsarState()
  const { isRbacUpdating } = rbacHelper()

  const valueChanged = async ([org, ab]: [string | undefined, boolean | undefined]) => {
    if (!org) {
      workspaceMap.value = {}
      resourceCounts.value = {}
      lastOrg = undefined
      return
    }
    if (ab) {
      return
    }

    if (org !== lastOrg) {
      await getWorkspaceMap(org)
    }
    lastOrg = org
  }

  watch([organization, isRbacUpdating], valueChanged)
  return valueChanged([initialState.organization, isRbacUpdating.value])
}
